import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import DraggableList, { DraggableItem, DragHandleProps } from '@repo/ui/draggable-list'
import { InformationIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { LanguageTestAnswerInput } from '../../../lib/types/models/language-test/language-test-answer'
import { LanguageTestQuestionInput } from '../../../lib/types/models/language-test/language-test-question'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import LanguageTestAnswerCreationItem from './language-test-answer-creation-item'

interface AnswerFormData extends DraggableItem {
  formId: string // Internal form ID for React keys
  answerId?: number // Database ID for existing answers
  label?: string
  position?: number
  isCorrect?: boolean
}

interface LanguageTestQuestionFormProps {
  onSubmit: (data: LanguageTestQuestionInput) => void
  onCancel: () => void
  defaultValues?: Partial<LanguageTestQuestionInput> & {
    answers?: LanguageTestAnswerInput[]
  }
  isEditing?: boolean
  isLoading?: boolean
  className?: string
}

const languageTestQuestionSchema: ZodType<LanguageTestQuestionInput> = z.object({
  label: createStringSchema('question'),
  position: createNumberSchema('position', {
    isPositive: true,
    isInteger: true
  }),
  answers: z
    .array(
      z.object({
        label: createStringSchema('une réponse'),
        isCorrect: z.boolean().default(false),
        position: z.number().optional()
      })
    )
    .min(2, 'Au moins 2 réponses sont requises')
    .max(10, 'Maximum 10 réponses autorisées')
})

const LanguageTestQuestionForm = ({
  onSubmit,
  onCancel,
  defaultValues,
  isEditing = false,
  isLoading = false,
  className
}: LanguageTestQuestionFormProps) => {
  const [answers, setAnswers] = useState<AnswerFormData[]>([])

  const form = useForm<LanguageTestQuestionInput>({
    mode: 'onSubmit',
    resolver: zodResolver(languageTestQuestionSchema),
    defaultValues: defaultValues || { label: '', position: 1, answers: [] }
  })

  const syncFormAnswers = useCallback(
    (newAnswers: AnswerFormData[]) => {
      setAnswers(newAnswers)
      form.setValue(
        'answers',
        newAnswers
          .filter((answer) => answer.label?.trim())
          .map((answer) => ({
            label: answer.label!,
            isCorrect: answer.isCorrect || false,
            position: answer.position || 0
          }))
      )
    },
    [form]
  )

  useEffect(() => {
    const initialAnswers: AnswerFormData[] = defaultValues?.answers?.length
      ? defaultValues.answers.map((answer, index) => ({
          id: 'id' in answer && typeof answer.id === 'number' ? answer.id : `temp-${index}`,
          formId: `answer-${index}-${Date.now()}`,
          answerId: 'id' in answer && typeof answer.id === 'number' ? answer.id : undefined,
          label: answer.label,
          isCorrect: !!answer.isCorrect,
          position: answer.position || index + 1
        }))
      : [
          // 2 empty options by default
          {
            id: 'temp-0',
            formId: `answer-0-${Date.now()}`,
            label: '',
            isCorrect: false,
            position: 1
          },
          {
            id: 'temp-1',
            formId: `answer-1-${Date.now() + 1}`,
            label: '',
            isCorrect: false,
            position: 2
          }
        ]

    syncFormAnswers(initialAnswers)
  }, [defaultValues, syncFormAnswers])

  const updateAnswer = useCallback(
    (formId: string, updates: Partial<AnswerFormData>) => {
      const newAnswers = answers.map((answer) => {
        if (answer.formId === formId) {
          return { ...answer, ...updates }
        }

        if (updates.isCorrect === true && answer.isCorrect) {
          return { ...answer, isCorrect: false }
        }
        return answer
      })
      syncFormAnswers(newAnswers)
    },
    [answers, syncFormAnswers]
  )

  const handleAddAnswer = useCallback(() => {
    const newAnswerFormId = `answer-${Date.now()}-${Math.random()}`
    const newAnswer: AnswerFormData = {
      id: `temp-${Date.now()}`,
      formId: newAnswerFormId,
      label: '',
      isCorrect: false,
      position: answers.length + 1
    }
    syncFormAnswers([...answers, newAnswer])
  }, [answers, syncFormAnswers])

  const handleDeleteAnswer = useCallback(
    (formId: string) => {
      syncFormAnswers(answers.filter((answer) => answer.formId !== formId))
    },
    [answers, syncFormAnswers]
  )

  const handleReorderAnswers = useCallback(
    (reorderedItems: DraggableItem[]) => {
      const reorderedAnswers = reorderedItems as AnswerFormData[]
      const answersWithPositions = reorderedAnswers.map((answer, index) => ({
        ...answer,
        position: index + 1
      }))
      syncFormAnswers(answersWithPositions)
    },
    [syncFormAnswers]
  )

  const handleFormSubmit = useCallback(
    async (data: LanguageTestQuestionInput) => {
      form.clearErrors()

      const isValid = await form.trigger('answers')
      if (!isValid) return

      const validAnswers = answers.filter((answer) => answer.label?.trim())

      if (validAnswers.length < 2) {
        form.setError('root.answers', {
          type: 'manual',
          message: 'Au moins 2 réponses avec du contenu sont requises'
        })
        return
      }

      if (!validAnswers.some((answer) => answer.isCorrect === true)) {
        form.setError('root.answers', {
          type: 'manual',
          message: 'Au moins une réponse doit être marquée comme correcte'
        })
        return
      }

      const dataToSubmit = {
        ...data,
        answers: validAnswers.map((answer) => ({
          ...(isEditing && answer.answerId && { id: answer.answerId }),
          label: answer.label!,
          isCorrect: !!answer.isCorrect,
          position: answer.position
        }))
      }

      onSubmit(dataToSubmit)
    },
    [form, onSubmit, answers, isEditing]
  )

  const formErrorMessage =
    form.formState.errors.answers?.message || form.formState.errors.root?.answers?.message

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6 flex flex-col">
          <div className={className}>
            <div className="flex items-center gap-2">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        required
                        label="Texte de la question"
                        type="text"
                        disabled={isLoading}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        required
                        label="Position"
                        type="number"
                        disabled={isLoading}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <Typography variant="tag">Options de réponse :</Typography>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddAnswer}
                  disabled={answers.length >= 10 || isLoading}
                  label="Ajouter une option"
                  size="small"
                />
              </div>

              {answers.length > 0 && (
                <DraggableList
                  items={answers}
                  onReorder={handleReorderAnswers}
                  disabled={isLoading}
                  renderItem={(
                    item: DraggableItem,
                    isDragging?: boolean,
                    dragHandleProps?: DragHandleProps
                  ) => {
                    const answer = item as AnswerFormData
                    const answerIndex = answers.findIndex((a) => a.formId === answer.formId)
                    const fieldError = form.formState.isSubmitted
                      ? form.formState.errors.answers?.[answerIndex]?.label
                      : undefined

                    return (
                      <LanguageTestAnswerCreationItem
                        isDragging={isDragging}
                        dragHandleProps={dragHandleProps}
                        key={answer.formId}
                        onUpdate={(label, isCorrect) => {
                          updateAnswer(answer.formId, { label, isCorrect })
                        }}
                        onDelete={() => handleDeleteAnswer(answer.formId)}
                        onCorrectChange={(isCorrect) => updateAnswer(answer.formId, { isCorrect })}
                        initialLabel={answer.label}
                        isCorrect={answer.isCorrect}
                        index={answerIndex}
                        hasError={!!fieldError}
                        errorMessage={fieldError?.message}
                        isLoading={isLoading}
                      />
                    )
                  }}
                  className="space-y-2"
                />
              )}

              {formErrorMessage && (
                <div className="text-red-500 text-sm bg-red-light border border-red-light-active px-3 py-2 rounded flex items-center gap-2">
                  <InformationIcon className="w-4 h-4" color={tailwindColors.red.DEFAULT} />
                  <Typography variant="label-semibold" className="text-red">
                    {formErrorMessage}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-t-white-active p-4 w-full">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isLoading}
              label="Fermer"
            />
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading || answers.length < 2}
              label={isEditing ? 'Modifier' : 'Valider'}
            />
          </div>
        </form>
      </Form>
    </div>
  )
}

export default LanguageTestQuestionForm
